import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { fmtNum, fmtINR } from '@/lib/api';
import { MapView } from '@/components/MapView';
import { SignaturePad } from '@/components/SignaturePad';
import { AiRouteBrief } from '@/components/AiRouteBrief';
import { Loading, StatusBadge } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MapPin, Phone, Navigation, Play, CheckCircle2, Camera, Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';

const NEXT = { driver_assigned: 'en_route', en_route: 'arrived', arrived: 'delivery_started' };
const NEXT_LABEL = { driver_assigned: 'Start Trip', en_route: 'Mark Arrived', arrived: 'Start Delivery' };

export default function DriverJob() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openVerify, setOpenVerify] = useState(false);
  const [ver, setVer] = useState({ delivered_qty: '', meter_reading: '', otp: '', signature: null, photo: null });
  const gpsTimer = useRef(null);

  const load = useCallback(() => api.get(`/orders/${orderId}`).then((r) => { setOrder(r.data); setVer((v) => ({ ...v, delivered_qty: v.delivered_qty || String(r.data.pricing.qty) })); }), [orderId]);
  useEffect(() => { load(); }, [load]);

  // simulate GPS ping while active
  const orderStatus = order?.status;
  useEffect(() => {
    if (orderStatus && ['en_route', 'arrived'].includes(orderStatus)) {
      gpsTimer.current = setInterval(() => {
        api.post('/driver/gps', { lat: 28.5563 + Math.random() * 0.02, lng: 77.3253 + Math.random() * 0.02 }).catch(() => { /* background ping; safe to ignore */ });
      }, 8000);
      return () => clearInterval(gpsTimer.current);
    }
  }, [orderStatus]);

  // Stable map props (avoids re-rendering the map on every parent render).
  const mapDriver = useMemo(() => (!order ? null : order.status === 'driver_assigned' ? { lat: 19.082, lng: 72.881 } : { lat: order.lat, lng: order.lng }), [order]);
  const mapDestination = useMemo(() => (order ? { lat: order.lat, lng: order.lng } : null), [order]);
  const mapOrigin = useMemo(() => ({ lat: 28.5563, lng: 77.3253 }), []); // depot: Sector 95, Noida

  if (!order) return <Loading />;
  const isProductOrder = order.order_type === 'products' || order.pricing?.order_type === 'products';

  const advance = async () => {
    const next = NEXT[order.status];
    setBusy(true);
    try { await api.post(`/driver/orders/${orderId}/advance`, { status: next }); toast.success(NEXT_LABEL[order.status] + ' done'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader(); reader.onload = () => setVer((v) => ({ ...v, photo: reader.result })); reader.readAsDataURL(file);
  };

  const complete = async () => {
    if (!ver.otp) return toast.error('Enter customer OTP');
    if (!ver.photo && !ver.signature) return toast.error('Capture signature or photo');
    setBusy(true);
    try {
      await api.post('/driver/verify-delivery', {
        order_id: orderId,
        delivered_qty: isProductOrder ? order.pricing.qty : Number(ver.delivered_qty),
        meter_reading: isProductOrder ? null : Number(ver.meter_reading),
        otp: ver.otp, signature: ver.signature, photo: ver.photo, lat: order.lat, lng: order.lng,
      });
      toast.success('Delivery completed & invoice generated');
      setOpenVerify(false); nav('/driver');
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); } finally { setBusy(false); }
  };

  const done = ['completed', 'invoice_generated'].includes(order.status);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h2 className="font-display text-xl font-semibold">{order.order_no}</h2><p className="text-sm text-muted-foreground">{order.customer_name}</p></div>
        <StatusBadge status={order.status} label={order.status_label} />
      </div>

      <MapView driver={mapDriver} destination={mapDestination} origin={mapOrigin} height="240px" />

      <div className="rounded-xl border border-border bg-card p-4 space-y-2 text-sm">
        <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-primary" /><span>{order.address_text}</span></div>
        {isProductOrder ? (
          <div className="space-y-1" data-testid="driver-product-items">
            <div className="flex items-center gap-2 font-medium"><Package className="h-4 w-4 text-primary" />Lube / DEF Order · {order.pricing.items?.length || 0} item(s)</div>
            {(order.pricing.items || []).map((it) => (
              <p key={it.product_id} className="text-xs text-muted-foreground pl-6">{it.name}{it.pack_size ? ` (${it.pack_size})` : ''} — {fmtNum(it.qty)} {it.unit}</p>
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2"><Package className="h-4 w-4 text-primary" />{fmtNum(order.pricing.qty)} {order.pricing.unit} · {order.pricing.product_name}</div>
        )}
        {order.instructions && <p className="text-muted-foreground text-xs">Note: {order.instructions}</p>}
        <div className="flex gap-2 pt-2">
          <Button variant="secondary" size="sm" className="flex-1" onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${order.lat},${order.lng}`, '_blank')} data-testid="driver-navigate-button"><Navigation className="h-4 w-4 mr-1" />Navigate</Button>
        </div>
      </div>

      {!done && <AiRouteBrief orderId={order.id} />}

      {!done && NEXT[order.status] && (
        <Button className="w-full h-12" onClick={advance} disabled={busy} data-testid="driver-advance-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4 mr-2" />{NEXT_LABEL[order.status]}</>}
        </Button>
      )}

      {order.status === 'delivery_started' && (
        <Button className="w-full h-12" onClick={() => setOpenVerify(true)} data-testid="driver-complete-button"><CheckCircle2 className="h-4 w-4 mr-2" />Complete Delivery</Button>
      )}

      {done && <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-3"><CheckCircle2 className="h-6 w-6 text-emerald-600" /><span className="font-medium">Delivery completed</span></div>}

      <Dialog open={openVerify} onOpenChange={setOpenVerify}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Delivery Verification</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {isProductOrder ? (
              <div className="rounded-lg border border-border bg-muted/40 p-3" data-testid="verify-items-list">
                <p className="text-sm font-medium mb-1">Confirm all items handed over:</p>
                {(order.pricing.items || []).map((it) => (
                  <p key={it.product_id} className="text-xs text-muted-foreground">• {it.name}{it.pack_size ? ` (${it.pack_size})` : ''} — {fmtNum(it.qty)} {it.unit}</p>
                ))}
                <p className="text-[11px] text-muted-foreground mt-2">Packed products are billed as ordered — no meter reading needed.</p>
              </div>
            ) : (
              <>
            <div>
              <Label>Final Delivered Quantity ({order.pricing.unit})</Label>
              <Input type="number" value={ver.delivered_qty} onChange={(e) => setVer({ ...ver, delivered_qty: e.target.value })} data-testid="verify-qty-input" />
              <p className="text-xs text-muted-foreground mt-1" data-testid="final-qty-hint">
                Ordered: {order.pricing.qty} {order.pricing.unit}. The tax invoice will be generated for the final quantity you enter here.
              </p>
              {Number(ver.delivered_qty) > 0 && Number(ver.delivered_qty) !== Number(order.pricing.qty) && (
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400 mt-1" data-testid="short-delivery-note">
                  Short/changed delivery: invoice will be billed for {ver.delivered_qty} {order.pricing.unit} (approx ₹{((Number(ver.delivered_qty) * order.pricing.unit_price) + (order.pricing.delivery_total || 0)).toLocaleString('en-IN', { maximumFractionDigits: 2 })})
                </p>
              )}
            </div>
            <div><Label>Meter/Dispensing Reading</Label><Input type="number" value={ver.meter_reading} onChange={(e) => setVer({ ...ver, meter_reading: e.target.value })} placeholder="e.g. 12345.6" data-testid="verify-meter-input" /></div>
              </>
            )}
            <div><Label>Customer OTP</Label><Input value={ver.otp} onChange={(e) => setVer({ ...ver, otp: e.target.value })} placeholder="6-digit from customer" data-testid="verify-otp-input" /></div>
            <div><Label>Delivery Photo</Label><label className="flex items-center gap-2 rounded-lg border border-dashed border-border p-3 cursor-pointer text-sm"><Camera className="h-4 w-4" />{ver.photo ? 'Photo captured' : 'Upload / capture photo'}<input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="verify-photo-input" /></label>{ver.photo && <img src={ver.photo} alt="proof" className="mt-2 h-24 rounded-lg object-cover" />}</div>
            <div><Label>Customer Signature</Label><SignaturePad onChange={(sig) => setVer((v) => ({ ...v, signature: sig }))} /></div>
          </div>
          <DialogFooter><Button onClick={complete} disabled={busy} className="w-full h-11" data-testid="verify-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm & Complete'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
